import {
  ButtonMenu,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  ModalProps,
  PageHeaderLayout,
  actionColumn,
  column,
  emptyCell,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { createTaskCatalogFormUrl } from "src/RouteUrls";
import { dateCell, priceCell } from "src/components";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  TaskCatalogLaborCostsPage_BidItemEstimateFragment,
  useTaskCatalogLaborCostsPageQuery,
} from "src/routes/libraries/task-catalog/TaskCatalogLaborCostsPage.generated";
import { LaborCostParams } from "src/routes/routesDef";
import { catalogTasksRoute } from "src/routing/domains/libraries/task-catalog/taskCatalogPaths";
import { queryResult } from "src/utils";
import { BidEstimateModal } from "../material-catalog/BidEstimateModal";

export function TaskCatalogLaborCostsPage() {
  const { globalPlanTaskId } = useRouteParams<LaborCostParams>();
  const { openModal } = useModal();
  const query = useTaskCatalogLaborCostsPageQuery({
    variables: { filter: { parent: [globalPlanTaskId] }, page: { offset: 0, limit: 100 } },
  });
  const maybeFetchMore = useCallback(async () => {
    if (query.data?.bidItemEstimates.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.bidItemEstimates.entities.length, limit: 100 } },
      });
    }
  }, [query]);
  const columns = useMemo(() => createColumns(openModal, globalPlanTaskId), [openModal, globalPlanTaskId]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Labor Costs",
        breadcrumbs: {
          breadcrumbs: [
            { href: catalogTasksRoute.url(), label: "Task Catalog" },
            { label: "Edit Task", href: createTaskCatalogFormUrl(globalPlanTaskId) },
          ],
        },
        actions: [
          {
            label: "Add",
            onClick: () => openModal({ content: <BidEstimateModal globalPlanTaskId={globalPlanTaskId} /> }),
          },
        ],
      }}
    >
      {queryResult(query, ({ bidItemEstimates }) => (
        <GridTableLayout
          hideEditColumns
          tableProps={{
            columns,
            rows: createRows(bidItemEstimates.entities),
            as: "virtual",
            infiniteScroll: { onEndReached: maybeFetchMore },
          }}
        />
      ))}
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: TaskCatalogLaborCostsPage_BidItemEstimateFragment };

type Row = HeaderRow | DataRow;

function createColumns(openModal: (props: ModalProps) => void, globalPlanTaskId: string): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Date Added", data: ({ createdAt }) => dateCell(createdAt), mw: "130px" }),
    column<Row>({ header: "Contributor", data: ({ createdBy }) => createdBy.name, w: "150px" }),
    column<Row>({ header: "Market", data: ({ markets }) => markets.map((m) => m.name).join(", "), mw: "160px" }),
    column<Row>({
      header: "Source",
      data: ({ sourceUrl }) =>
        sourceUrl && (
          <Link to={sourceUrl} target="_blank">
            {sourceUrl}
          </Link>
        ),
      mw: "240px",
    }),
    column<Row>({
      header: "UoM",
      data: ({ bidItemEstimate }) => bidItemEstimate?.unitOfMeasure.abbreviation,
      mw: "80px",
    }),
    column<Row>({
      header: "Cost",
      data: ({ costInMills }) =>
        priceCell({
          valueInCents: costInMills / 10,
          minDecimalPlaces: 3,
          maxDecimalPlaces: 3,
          trim: true,
          alignment: "left",
        }),
      mw: "120px",
    }),
    actionColumn<Row>({
      header: emptyCell,
      w: "50px",
      data: (row) => (
        <ButtonMenu
          trigger={{ icon: "verticalDots" }}
          items={[
            {
              label: "Edit",
              onClick: () =>
                openModal({ content: <BidEstimateModal globalPlanTaskId={globalPlanTaskId} bidEstimate={row} /> }),
            },
          ]}
        />
      ),
    }),
  ];
}

function createRows(data: TaskCatalogLaborCostsPage_BidItemEstimateFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...data?.map((bie) => ({ kind: "data" as const, id: bie.id, data: bie }))];
}
